import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, css, TemplateResult, nothing } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';

import { SlotController } from '../../controllers/slot-controller';

import componentStyles from './ghs-in-page-hero.css.lit';
import '../../base/outline-container/outline-container';

import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';

export type BackgroundColor =
  | 'green'
  | 'green-gradient'
  | 'blue'
  | 'blue-gradient'
  | 'blue-green-gradient'
  | 'light-blue'
  | 'light_blue'
  | 'light-gray'
  | 'light_gray'
  | 'white'
  | 'transparent';
export type CTAPosition = 'top' | 'bottom';
/**
 * The ghs-in-page-hero component.
 * @element ghs-in-page-hero
 * @slot eyebrow - eyebrow text
 * @slot title - title text
 * @slot image - image
 * @slot body - ghs-styled-text body content
 *    - 'default' variant on dark-colored heros
 *    - 'dark' variant on light-colored heros
 * @slot cta-links - ghs-button CTA link list
 *    - 'tertiary' variant on light-colored heros
 *    - 'primary' variant on dark-colored heros
 */
@customElement('ghs-in-page-hero')
export class GhsInPageHero extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, css``];

  slots = new SlotController(this, true);

  // eslint-disable-next-line
  // @ts-ignore
  private _screenSizeController = new ScreenSizeController(this);

  /**
   * Flips the image and content positioning.
   */
  @property({ type: Boolean, attribute: 'is-flipped', reflect: true })
  isFlipped = false;

  /**
   * Controls where the CTA links will appear.
   */
  @property({ type: String, attribute: 'cta-position', reflect: true })
  ctaPosition: CTAPosition = 'bottom';

  /**
   * Changes the background-color of the component
   * Note: light-blue variant is for specific use cases only!
   * @param BackgroundColor
   * @default 'light-gray'
   */
  @property({ type: String, attribute: 'background-color', reflect: true })
  backgroundColor: BackgroundColor = 'light-gray';

  @state() hasEyebrowSlot: boolean;
  @state() hasTitleSlot: boolean;
  @state() hasBodySlot: boolean;
  @state() hasCtaLinksSlot: boolean;
  @state() hasImageSlot: boolean;

  firstUpdated(): void {
    super.firstUpdated();
    this.hasEyebrowSlot = this.slots.test('eyebrow');
    this.hasTitleSlot = this.slots.test('title');
    this.hasBodySlot = this.slots.test('body');
    this.hasCtaLinksSlot = this.slots.test('cta-links');
    this.hasImageSlot = this.slots.test('image');
  }

  updated(): void {
    const isMobile: boolean =
      window.innerWidth < (screenSizeMap.get('lg') as number);
    const ctaOnTop: boolean = this.hasCtaLinksSlot && this.ctaPosition == 'top';

    if (ctaOnTop) {
      this.shadowRoot
        ?.querySelectorAll('.in-page-hero-ctas ghs-button')
        .forEach(element => {
          if (isMobile) {
            element.setAttribute('container-fit-variant', '');
            element.setAttribute('justify-variant', 'between');
          } else {
            element.removeAttribute('container-fit-variant');
            element.removeAttribute('justify-variant');
          }
        });
    }
  }
  render(): TemplateResult {
    const ctaOnTop: boolean = this.hasCtaLinksSlot && this.ctaPosition == 'top';
    const ctaOnBottom: boolean =
      this.hasCtaLinksSlot && this.ctaPosition == 'bottom';

    return html`
      <div class="in-page-hero bg-${this.backgroundColor}">
        <outline-container x-padding>
          <div class="in-page-hero-inner ${this.isFlipped ? 'is-flipped' : ''}">
            <div class="in-page-hero-content">
              ${ctaOnTop
                ? html`
                    <div class="in-page-hero-ctas">
                      <slot name="cta-links"></slot>
                    </div>
                  `
                : nothing}
              ${this.hasEyebrowSlot
                ? html`
                    <div class="in-page-hero-eyebrow">
                      <slot name="eyebrow"></slot>
                    </div>
                  `
                : nothing}
              ${this.hasTitleSlot
                ? html`
                    <div class="in-page-hero-title">
                      <slot name="title"></slot>
                    </div>
                  `
                : nothing}
              ${this.hasBodySlot
                ? html`
                    <div class="in-page-hero-body">
                      <slot name="body"></slot>
                    </div>
                  `
                : nothing}
              ${ctaOnBottom
                ? html`
                    <div class="in-page-hero-ctas">
                      <slot name="cta-links"></slot>
                    </div>
                  `
                : nothing}
            </div>
            ${this.hasImageSlot
              ? html`
                  <div
                    class="in-page-hero-image ${this.isFlipped
                      ? 'margin-right'
                      : 'margin-left'}"
                  >
                    <slot name="image"></slot>
                  </div>
                `
              : nothing}
          </div>
        </outline-container>
      </div>
    `;
  }
}
